import { useLayoutEffect, useRef, useState, useTransition } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '@/components/ui/Dropdown';
import Button from '@/components/ui/Button';
import icFilterExpander from '@/assets/icons/filterExpander.svg';
import styles from '@/components/filters/OptionsRangeFilter/OptionsRangeFilter.module.css';
import icArrowRight from '@/assets/icons/arrowRight.svg';
import icCustomRangeExpander from '@/assets/icons/customRangeExpander.svg';
import icCustomRangeExpanderActive from '@/assets/icons/customRangeExpander-active.svg';
import icShekel from '@/assets/icons/shekel.svg';
import icCalendar from '@/assets/icons/calendar.svg';
import icprevMonth from '@/assets/icons/prevMonth.svg';
import icnextMonth from '@/assets/icons/nextMonth.svg';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { he } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import { RefObject } from 'react';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import Radio from '@/components/ui/Radio/Radio';
import { formatDate, formatISODate } from '@/helpers/dateHelper';
import {
  civilIsoFromLocalDate,
  formatHebrewDayFromLocalCivilDate,
  formatHebrewFromLocalCivilDate,
  formatHebrewYearGematriya,
  getHebrewDateParts,
  hebrewMonthYearFromCivilDate,
  hebrewYearJumpAnchorFromParts,
  HebrewMonthYear,
  resolveHebrewPickerHeader,
  selectableHebrewYears,
} from '@/helpers/hebrewDateHelper';
import useCalendarMode from '@/hooks/useCalendarMode';
import { formatDatePickerWeekDay } from '@/helpers/datePickerWeekDay';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import CalendarModeToggle from '@/components/ui/DataTable/CalendarModeToggle';
import { CalendarMode } from '@/helpers/calendarMode';

registerLocale('he', he);

interface InputWithIconProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  onClick?: () => void;
  icon?: string;
  iconClassName?: string;
  inputClassName?: string;
  type?: React.HTMLInputTypeAttribute;
  onIconClick?: () => void;
  ref?: RefObject<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

const InputWithIcon = ({
  value,
  onChange,
  placeholder,
  onClick,
  icon,
  iconClassName,
  inputClassName,
  type = 'text',
  onIconClick,
  ref,
  onKeyDown,
  onBlur,
  onFocus,
}: InputWithIconProps) => (
  <div className={styles.rangeInputContainer}>
    {icon && <img src={icon} className={iconClassName} onClick={onIconClick || onClick} />}
    <input
      ref={ref}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`${styles.rangeInput} ${inputClassName || ''}`}
      onClick={onClick}
      onKeyDown={onKeyDown}
      onBlur={onBlur}
      onFocus={onFocus}
    />
  </div>
);

interface CustomDateInputProps {
  value: string;
  onClick?: () => void;
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ref?: RefObject<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
}

/*
 * react-datepicker clones this element with its own value/onChange/onKeyDown/onBlur/onFocus.
 * Every one of them has to reach the <input>: dropping onKeyDown alone would leave Enter and
 * Escape dead inside the field (HAT-1154).
 */
const CustomDateInput = ({ value, onClick, placeholder, onChange, ref, onKeyDown, onBlur, onFocus }: CustomDateInputProps) => (
  <InputWithIcon
    ref={ref}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    onClick={onClick}
    icon={icCalendar}
    iconClassName={styles.calendarIcon}
    inputClassName={styles.dateInput}
    onIconClick={onClick}
    onKeyDown={onKeyDown}
    onBlur={onBlur}
    onFocus={onFocus}
  />
);

CustomDateInput.displayName = 'CustomDateInput';

/*
 * HAT-1212: כפתורי החודש הקודם/הבא בכותרת הלוח היו img עם onClick, בלי
 * מצב לחוץ, פוקוס-מקלדת או שהות בזמן הרינדור. משתמש שלחץ פעם ושלוש ואל
 * הלוח הכבד מבין ה-312px לא הספיק להיפתח - PostHog סימן את זה כזעם
 * (144/18 לחצני "הקודם" ב-14 ימים על /new/:id/transactions לבד).
 *
 * הכפתור כאן:
 *  - button אמיתי, כלומר Enter/Space ופוקוס נראה;
 *  - startTransition על ה-decrease/increase כדי שהלחיצה הבאה תוכל
 *    לקטוע את הרינדור הקודם במקום להצטבר מאחוריו;
 *  - aria-busy למקרא-מסך בזמן ה-transition;
 *  - state לחוץ בממשל CSS (:active + aria-busy), כך שלחיצה נראית מיד.
 *
 * ⚠️ ה-img נשאר עם alt='' בכוונה: הכפתור מספק את השם הנגיש, ותווית
 * כפולה הייתה גורמת לקורא-מסך להקריא "הקודם, הקודם, לחצן".
 */
interface DatepickerNavButtonProps {
  icon: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

const DatepickerNavButton = ({ icon, label, onClick, disabled }: DatepickerNavButtonProps) => {
  const [isPending, startTransition] = useTransition();

  return (
    <button
      type="button"
      aria-label={label}
      aria-busy={isPending || undefined}
      disabled={disabled}
      className={styles.datepickerNavButton}
      onClick={(event) => {
        event.preventDefault();
        startTransition(() => onClick());
      }}
    >
      <img src={icon} alt="" className={styles.datepickerNavIcon} />
    </button>
  );
};

export interface FilterOption {
  id: string;
  label: string;
  checked: boolean;
  /**
   * טקסט קטן אחרי התווית, מחוץ לתרגום — למשל מונה "(3)". התווית עצמה
   * עוברת דרך t(), ולכן מונה שהודבק אליה היה שובר את מפתח התרגום.
   */
  suffix?: string;
}

export type SelectionType = 'checkbox' | 'radio';

export interface RangeFilterConfig {
  type: 'number' | 'date';
  fromPlaceholder: string;
  toPlaceholder: string;
}

export type FilterType = 'options' | 'range' | 'options-and-range';

export interface OptionsRangeFilterProps {
  title: string;
  filterType: FilterType;

  //for options filter
  options?: FilterOption[];
  onOptionChange?: (id: string) => void;
  selectionType?: SelectionType;
  /**
   * "בחר הכל" / "הסר הכל" (HAT-1170). מוצג רק במסך צר (מתחת ל-1100,
   * כמו כל שאר סגנון הכרטיסים והפילטרים) ורק ל-checkbox — ל-radio אין
   * משמעות ל"בחר הכל". מכוון: לא מיושם כלולאה של onOptionChange על כל
   * אפשרות, כי כל אחד מהקריאות המרכיבות את הפילטר הזה (ר' PaymentMethodFilter
   * וכו') קורא ל-state הנוכחי מתוך closure ולא מ-updater פונקציונלי —
   * קריאה סינכרונית חוזרת הייתה גורמת לכל הקריאות לדרוס זו את זו וכל
   * הבחירה מסתיימת עם רק השינוי האחרון בתוקף. ה-caller מקבל isSelectAll
   * ומבצע עדכון state יחיד, נכון.
   */
  onSelectAllChange?: (selectAll: boolean) => void;

  //for range filter
  rangeConfig?: RangeFilterConfig;
  fromValue?: string;
  setFromValue?: (value: string) => void;
  toValue?: string;
  setToValue?: (value: string) => void;
  appliedRange?: { from: string; to: string } | null;
  setAppliedRange?: (range: { from: string; to: string } | null) => void;
  onRangeApply?: (from: string, to: string) => void;

  //for month navigation arrows (only for date range)
  showMonthNavigation?: boolean;

  /**
   * HAT-1212: העמוד מדווח שהוא באמצע רענון תוצאות. חצי החודש בלוח
   * וחצי הניווט החיצוני מקבלים אפקט disabled + שקיפות כדי שלא ייקלטו
   * לחיצות נוספות בזמן שהעמוד עדיין מסתדר. אין השפעה על הלוגיקה של
   * הפילטר עצמו — רק על הפידבק הוויזואלי.
   */
  isBusy?: boolean;
  /**
   * Transactions date filter (HAT-1210 / HAT-1249): dual Gregorian/Hebrew
   * day cells, a year-jump chip (Gregorian `2026 ▾` / Hebrew `תשפ״ו ▾`),
   * and a compact לועזי|עברי control in the open picker chrome. The board
   * toolbar still has the same switch; both write `calendarMode`.
   * Other boards leave this off.
   */
  hebrewCalendar?: boolean;
}

export const isFullMonth = (from: Date, to: Date): boolean => {
  const monthStart = new Date(from.getFullYear(), from.getMonth(), 1);
  const monthEnd = new Date(from.getFullYear(), from.getMonth() + 1, 0);
  return (
    from.getDate() === monthStart.getDate() &&
    to.getDate() === monthEnd.getDate() &&
    from.getFullYear() === to.getFullYear() &&
    from.getMonth() === to.getMonth()
  );
};

const getMonthRange = (date: Date): { from: string; to: string } => {
  const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
  const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  return { from: formatISODate(monthStart), to: formatISODate(monthEnd) };
};

const DEFAULT_FROM_DATE = '2000-01-01';
const DEFAULT_TO_DATE = () => new Date().toISOString().split('T')[0];

const EARLIEST_SELECTABLE_YEAR = 2000;

const DATE_DISPLAY_FORMAT = 'dd/MM/yyyy';

const DATE_SEGMENT_SIZES = [2, 2, 4];
const SHORT_YEAR_CENTURY = 2000;

/*
 * Splits what was typed into day, month and year. Digits fill a segment until it is full and then
 * spill into the next, so a date can be typed as bare digits with no separators at all; a
 * separator typed by hand closes the segment it follows early, which is what keeps 1/8/2026 the
 * first of August rather than the eighteenth of month twenty (HAT-1154).
 */
const typedDateSegments = (typed: string): string[] => {
  /* Text with no digits in it is not a date half typed, it is a field that should read empty. */
  if (!/[0-9]/.test(typed)) {
    return [''];
  }

  const segments = [''];

  for (const character of typed) {
    const last = segments.length - 1;

    if (!/[0-9]/.test(character)) {
      /*
       * A separator closes the segment it follows even when that segment is empty, so that the
       * gap left by a deleted day stays a gap - otherwise the month slides into its place and
       * 18/08/2026 with its day rubbed out reads as the 8th of month 20.
       */
      if (segments.length < DATE_SEGMENT_SIZES.length) {
        segments.push('');
      }
      continue;
    }

    if (segments[last].length === DATE_SEGMENT_SIZES[last]) {
      /* Past the end of the year there is nowhere left to put a digit, so it is dropped. */
      if (segments.length === DATE_SEGMENT_SIZES.length) {
        continue;
      }
      segments.push('');
    }

    segments[segments.length - 1] += character;
  }

  return segments;
};

/*
 * The separators are the field's to insert, so that nobody has to type them (HAT-1154). A trailing
 * one is kept: it can only be there because it was typed by hand, and dropping it would take the
 * segment boundary with it - 1/ followed by 8 would collapse back into the 18th.
 */
const maskTypedDate = (typed: string): string => typedDateSegments(typed).join('/');

/*
 * Masking what sits before the caret says where the caret now belongs: a separator the field put
 * in pushes it along, and one typed by hand leaves it after the separator rather than in front of
 * it - where the next digit would have gone back into the segment just closed.
 */
const caretAfterMasking = (typed: string, caret: number, masked: string): number => Math.min(maskTypedDate(typed.slice(0, caret)).length, masked.length);

/*
 * A date is read only once its year is whole: two digits for this century, or four as typed.
 * Reading a year still being typed is what made 18/08/2026 settle on 2002 the moment the "2"
 * landed, and then refuse the keystrokes that would have finished it (HAT-1154).
 */
const parseTypedDate = (typed: string): Date | null => {
  const [dayText = '', monthText = '', yearText = ''] = typedDateSegments(typed);
  if (!dayText || !monthText || (yearText.length !== 2 && yearText.length !== 4)) {
    return null;
  }

  const day = Number(dayText);
  const month = Number(monthText);
  const year = yearText.length === 2 ? SHORT_YEAR_CENTURY + Number(yearText) : Number(yearText);

  /* new Date rolls 31/02 on into March, and a date that rolled is not the date that was typed. */
  const date = new Date(year, month - 1, day);
  const isAsTyped = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
  return isAsTyped ? date : null;
};

const monthNames = (locale: string): string[] => Array.from({ length: 12 }, (_, month) => new Date(2000, month, 1).toLocaleString(locale, { month: 'long' }));

/* Newest first - the dates people filter by are almost always recent ones. */
const selectableYears = (shownYear: number): number[] => {
  const latest = Math.max(new Date().getFullYear() + 1, shownYear);
  const earliest = Math.min(EARLIEST_SELECTABLE_YEAR, shownYear);
  return Array.from({ length: latest - earliest + 1 }, (_, offset) => latest - offset);
};

type RangeMode = 'range' | 'exact';

/* An applied range whose two ends are equal is what "exact" produces, and the only thing it can be. */
const isExactRange = (range: { from: string; to: string } | null): boolean => !!range && !!range.from && range.from === range.to;

export const OptionsRangeFilter = ({
  title,
  filterType,
  options = [],
  onOptionChange,
  onSelectAllChange,
  selectionType = 'checkbox',
  rangeConfig,
  fromValue = '',
  setFromValue = () => {},
  toValue = '',
  setToValue = () => {},
  appliedRange = null,
  setAppliedRange = () => {},
  onRangeApply,
  showMonthNavigation = false,
  isBusy = false,
  hebrewCalendar = false,
}: OptionsRangeFilterProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile } = useDeviceDetection();
  const [calendarMode, setCalendarMode] = useCalendarMode();
  const hebrewPrimary = hebrewCalendar && calendarMode === 'hebrew';
  const [isCustomRangeVisible, setIsCustomRangeVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [rangeMode, setRangeMode] = useState<RangeMode>('range');
  const [hebrewYearJump, setHebrewYearJump] = useState<HebrewMonthYear | null>(null);

  const handlePickerCalendarMode = (mode: CalendarMode) => {
    setHebrewYearJump(null);
    setCalendarMode(mode);
  };

  /*
   * What each date field is showing while it is being typed, held here rather than left to
   * react-datepicker, which reformats the field from under the caret as soon as it can parse it.
   */
  const [typedDates, setTypedDates] = useState<{ from: string | null; to: string | null }>({ from: null, to: null });
  const clearTypedDates = () => setTypedDates({ from: null, to: null });

  /*
   * Inserting a separator moves every character after it along, and React puts the caret at the
   * end of a field whose value it rewrote. Left alone that makes the date unfixable once typed:
   * correcting the day would send the caret to the year. So the caret is put back where the digit
   * it was behind ended up.
   */
  const caretToRestore = useRef<{ field: HTMLInputElement; position: number } | null>(null);

  useLayoutEffect(() => {
    const pending = caretToRestore.current;
    if (!pending) {
      return;
    }

    caretToRestore.current = null;
    pending.field.setSelectionRange(pending.position, pending.position);
  });

  /*
   * HAT-1155: one value, one field. Searching for a single amount or a single day used to mean
   * typing it into both ends of the range, so "exact" collapses the panel to one input and applies
   * the value to both ends itself. The range mode is untouched, one-sided bounds included.
   */
  const describeRange = (range: { from: string; to: string }): string => {
    const isDate = rangeConfig?.type === 'date';
    const unit = (amount: string) => (isDate ? amount : `${amount} ש"ח`);

    if (isExactRange(range)) {
      return isDate ? formatDate(new Date(range.from)) : unit(range.from);
    }
    if (isDate) {
      return `${formatDate(new Date(range.to))} - ${formatDate(new Date(range.from))}`;
    }
    if (range.from && !range.to) {
      return t('amountFromAndAbove', { amount: range.from });
    }
    if (!range.from && range.to) {
      return t('amountUpTo', { amount: range.to });
    }
    return `${unit(range.from)} - ${unit(range.to)}`;
  };

  const hasValue = (): boolean => {
    switch (filterType) {
      case 'options':
        return options.some((option) => option.checked);
      case 'options-and-range':
        return options.some((option) => option.checked) || !!appliedRange;
      case 'range':
        return !!appliedRange;
      default:
        return false;
    }
  };

  const getDynamicTitle = () => {
    const checkedOptions = options.filter((option) => option.checked);
    if (checkedOptions.length > 0) {
      if (checkedOptions.length === 1) {
        return t(checkedOptions[0].label);
      } else {
        return `${t(checkedOptions[0].label)} + ${checkedOptions.length - 1}`;
      }
    }

    if (appliedRange) {
      if (rangeConfig?.type === 'date') {
        const from = new Date(appliedRange.from);
        const to = new Date(appliedRange.to);

        /* A single day is short enough to name outright, and far more useful than "custom range". */
        if (isExactRange(appliedRange)) {
          return formatDate(from);
        }

        if (isFullMonth(from, to)) {
          return from.toLocaleDateString(i18n.language === 'he' ? 'he-IL' : 'en-US', {
            month: 'long',
            year: 'numeric',
          });
        }

        return t('customRange');
      }
      return describeRange(appliedRange);
    }

    return t(title);
  };

  const resetRangeValuesToApplied = () => {
    clearTypedDates();

    if (!appliedRange) {
      setFromValue('');
      setToValue('');
    } else {
      setFromValue(appliedRange.from);
      setToValue(appliedRange.to);
    }
  };

  const handleSetDropdownState = (isOpen: boolean) => {
    setIsOpen(isOpen);

    if (isOpen) {
      clearTypedDates();
      /* Reopen showing the mode the applied value came from, so it can be corrected in place. */
      setRangeMode(isExactRange(appliedRange) ? 'exact' : 'range');
      return;
    }

    setHebrewYearJump(null);

    if (filterType === 'range') {
      resetRangeValuesToApplied();
    } else if (filterType === 'options-and-range' && isCustomRangeVisible) {
      resetRangeValuesToApplied();
      setIsCustomRangeVisible(false);
    }
  };

  const switchRangeMode = (mode: RangeMode) => {
    setRangeMode(mode);
    clearTypedDates();
    /* Carry whichever end was already typed into the single field, rather than making them retype it. */
    if (mode === 'exact') {
      if (!fromValue && toValue) {
        setFromValue(toValue);
      }
      setToValue('');
    }
  };

  const handleRangeApply = () => {
    let finalFrom = fromValue;
    let finalTo = toValue;

    if (rangeMode === 'exact') {
      finalTo = finalFrom;
      setToValue(finalTo);
    } else if (rangeConfig?.type === 'date') {
      if (!finalFrom) {
        finalFrom = DEFAULT_FROM_DATE;
        setFromValue(finalFrom);
      }

      if (!finalTo) {
        finalTo = DEFAULT_TO_DATE();
        setToValue(finalTo);
      }
    }

    onRangeApply?.(finalFrom, finalTo);
    setAppliedRange({ from: finalFrom, to: finalTo });
    setIsOpen(false);
    if (filterType === 'options-and-range') {
      setIsCustomRangeVisible(false);
    }
  };

  const allOptionsSelected = options.length > 0 && options.every((option) => option.checked);

  const renderOptionsContent = () => (
    <>
      {isMobile && selectionType === 'checkbox' && onSelectAllChange && options.length > 1 && (
        <button type="button" className={styles.selectAllOption} onClick={() => onSelectAllChange(!allOptionsSelected)}>
          <span className="body-md-semibold text-(--font-tertiary)">{t(allOptionsSelected ? 'deselectAll' : 'selectAll')}</span>
        </button>
      )}
      {options.map((option) => (
        <label key={option.id} className={`${styles.filterOption} ${option.checked ? styles.filterOptionChecked : ''}`}>
          {selectionType === 'checkbox' ? (
            <Checkbox checked={option.checked} onCheckedChange={() => onOptionChange?.(option.id)} />
          ) : (
            <Radio
              checked={option.checked}
              onChange={() => {
                if (filterType === 'options-and-range') {
                  setAppliedRange(null);
                  setFromValue('');
                  setToValue('');
                }
                onOptionChange?.(option.id);
                // One answer is the whole answer: a chosen preset closes the menu, the way a
                // select does. Checkboxes stay open, because the next tick is usually coming.
                setIsOpen(false);
              }}
            />
          )}
          <span className={`${option.checked ? 'body-md-semibold' : 'body-md-regular'} text-(--font-body-1)`}>
            {t(option.label)}
            {option.suffix && (
              <span className="body-sm-regular text-(--font-body-2)" style={{ marginInlineStart: 4 }}>
                {option.suffix}
              </span>
            )}
          </span>
        </label>
      ))}
      {filterType === 'options-and-range' && (
        <div className={`${styles.customRangeOption} ${appliedRange ? 'bg-(--surface-secondary-lowest)' : ''}`} onClick={() => setIsCustomRangeVisible(true)}>
          <span className={`${styles.customRangeText} ${appliedRange ? 'body-md-medium text-(--font-body-5)' : 'body-md-semibold text-(--font-body-1)'}`}>
            {appliedRange ? describeRange(appliedRange) : t('customRange')}
          </span>
          <img src={appliedRange ? icCustomRangeExpanderActive : icCustomRangeExpander} alt="arrow" />
        </div>
      )}
    </>
  );

  const renderRangeContent = () => {
    if (!rangeConfig) return null;

    const isRangeMode = rangeMode === 'range';

    const renderRangeInput = (value: string, setValue: (value: string) => void, placeholder: string, isStartValue = false) => {
      switch (rangeConfig.type) {
        case 'date': {
          const end: 'from' | 'to' = isStartValue ? 'from' : 'to';
          const selectedDate = value ? new Date(value) : null;
          const setTypedDate = (text: string | null) => setTypedDates((current) => (end === 'from' ? { ...current, from: text } : { ...current, to: text }));

          /* Only the calendar reaches this now; typed text is handled in onChangeRaw. */
          const handleDateChange = (date: Date | null) => {
            setTypedDate(null);

            if (!date) {
              setValue('');
              return;
            }

            if (isNaN(date.getTime())) {
              return;
            }

            setValue(formatISODate(date));
          };

          const handleTyping = (field: HTMLInputElement) => {
            const raw = field.value;
            const caret = field.selectionStart ?? raw.length;

            /*
             * A field can already be showing a date - this filter opens with one applied - and a
             * click lands the caret in the middle of it. The first keystroke there starts a new
             * date rather than being threaded into the old one, which interleaved the two into
             * nonsense like 01/80/8268 (HAT-1154). Only added characters count: replacing a
             * selection, or backspacing, is a deliberate edit of what is already there.
             */
            const shownBefore = typedDates[end] ?? (selectedDate ? formatDate(selectedDate) : '');
            const addedCount = raw.length - shownBefore.length;
            const isNewDate = typedDates[end] === null && addedCount > 0;

            const typed = isNewDate ? raw.slice(Math.max(caret - addedCount, 0), caret) : raw;
            const typedCaret = isNewDate ? typed.length : caret;
            const masked = maskTypedDate(typed);

            caretToRestore.current = { field, position: caretAfterMasking(typed, typedCaret, masked) };
            setTypedDate(masked);

            /*
             * A half-typed date is no date: holding on to the last one that parsed would leave a
             * date nobody finished typing as the one that gets applied.
             */
            const parsed = parseTypedDate(masked);
            setValue(parsed ? formatISODate(parsed) : '');
          };

          return (
            <div className={styles.datepickerWrapper}>
              <DatePicker
                selected={selectedDate}
                onChange={handleDateChange}
                customInput={<CustomDateInput placeholder={t(placeholder)} value="" onChange={() => {}} />}
                dateFormat={DATE_DISPLAY_FORMAT}
                /* The text in the field is ours; left to itself react-datepicker rewrites it mid-word. */
                value={typedDates[end] ?? (selectedDate ? formatDate(selectedDate) : '')}
                locale={i18n.language}
                placeholderText={t(placeholder)}
                formatWeekDay={formatDatePickerWeekDay}
                /*
                 * Beside the panel, not under the field: at bottom-start the 312px calendar
                 * covered the second date field and the apply button, so once it opened there
                 * was no way left to finish the range (HAT-1154). It flips on its own when
                 * there is no room on that side.
                 *
                 * HAT-1218: מתחת ל-$tablet (מובייל/פולד/טאבלט) הלוח לא ממוקם ליד השדה
                 * אלא נפתח כמודל מסך מלא (`withPortal`), כי כל תצוגת popper של 312px
                 * מסך של 340px (Z Fold cover) חתכה את הלוח וגם את כפתור ה-Apply.
                 * הגלובל CSS של react-datepicker כבר נותן backdrop 100vw/100vh —
                 * ה-CSS המקומי כאן מתאים אותו למותג ולנגישות (≥44 targets, בורדו).
                 */
                withPortal={isMobile}
                popperPlacement="left-start"
                calendarClassName={`${styles.datepickerCalendar} ${hebrewCalendar ? `${styles.datepickerCalendarDual} hebrew-calendar-dual` : ''}`}
                showPopperArrow={false}
                /*
                 * Only a range has two ends that bound each other. In exact mode the single field is
                 * rendered as the start one while toValue still holds the applied value, so leaving
                 * these unconditional capped the calendar at the date already applied - the filter
                 * could never be moved forward to a later day (HAT-1155).
                 */
                minDate={isRangeMode && !isStartValue && fromValue ? new Date(fromValue) : undefined}
                maxDate={isRangeMode && isStartValue && toValue ? new Date(toValue) : undefined}
                onChangeRaw={(event) => {
                  /* Fired for a calendar click as well, where there is no text to read. */
                  if (event?.target instanceof HTMLInputElement) {
                    handleTyping(event.target);
                  }

                  /* The parsing is ours too - react-datepicker must not also parse what was typed. */
                  event?.preventDefault();
                }}
                renderCustomHeader={({ date, changeMonth, changeYear, decreaseMonth, increaseMonth }) => {
                  const hebrewHeader = hebrewPrimary ? resolveHebrewPickerHeader(date, hebrewYearJump) : null;
                  const jumpToHebrewYear = (hebrewYear: number) => {
                    if (!hebrewHeader) return;
                    const viewed = hebrewMonthYearFromCivilDate(date);
                    const keepDay = viewed?.month === hebrewHeader.month && viewed.year === hebrewHeader.year;
                    const day = keepDay ? (getHebrewDateParts(civilIsoFromLocalDate(date))?.day ?? 1) : 1;
                    const next = hebrewYearJumpAnchorFromParts(hebrewHeader.month, day, hebrewYear);
                    if (!next) return;
                    setHebrewYearJump(hebrewMonthYearFromCivilDate(next));
                    changeYear(next.getFullYear());
                    changeMonth(next.getMonth());
                  };
                  const stepGregorianMonth = (step: () => void) => {
                    setHebrewYearJump(null);
                    step();
                  };

                  return (
                    <div className={styles.datepickerChrome}>
                      {hebrewCalendar && (
                        <div
                          className={styles.datepickerCalendarMode}
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <CalendarModeToggle value={calendarMode} onChange={handlePickerCalendarMode} embedded />
                        </div>
                      )}
                      <div className={styles.datepickerMonthHeader} data-calendar-header={hebrewHeader ? 'hebrew' : 'gregorian'}>
                        <DatepickerNavButton icon={icprevMonth} label={t('previous')} onClick={() => stepGregorianMonth(decreaseMonth)} disabled={isBusy} />
                        <div className={styles.datepickerHeaderSelects}>
                          {hebrewHeader ? (
                            <>
                              <span className={`${styles.datepickerHebrewMonth} body-md-bold text-(--neutral-900)`} data-testid="hebrew-month-title">
                                {hebrewHeader.monthName}
                              </span>
                              <select
                                className={`${styles.datepickerHeaderSelect} ${styles.datepickerHeaderYearSelect} ${styles.yearJumpChip} ${styles.yearJumpChipHebrew} body-md-semibold`}
                                aria-label={t('year')}
                                title={t('yearJump')}
                                data-testid="year-jump"
                                value={hebrewHeader.year}
                                onChange={(e) => jumpToHebrewYear(Number(e.target.value))}
                              >
                                {selectableHebrewYears(hebrewHeader.year).map((year) => (
                                  <option key={year} value={year}>
                                    {formatHebrewYearGematriya(year)}
                                  </option>
                                ))}
                              </select>
                            </>
                          ) : (
                            <>
                              <select
                                className={`${styles.datepickerHeaderSelect} body-md-bold text-(--neutral-900)`}
                                aria-label={t('month')}
                                value={date.getMonth()}
                                onChange={(e) => changeMonth(Number(e.target.value))}
                              >
                                {monthNames(i18n.language).map((name, index) => (
                                  <option key={name} value={index}>
                                    {name}
                                  </option>
                                ))}
                              </select>
                              <select
                                className={`${styles.datepickerHeaderSelect} ${styles.datepickerHeaderYearSelect} ${styles.yearJumpChip} body-md-semibold text-(--neutral-900)`}
                                aria-label={t('year')}
                                title={t('yearJump')}
                                data-testid="year-jump"
                                value={date.getFullYear()}
                                onChange={(e) => changeYear(Number(e.target.value))}
                              >
                                {selectableYears(date.getFullYear()).map((year) => (
                                  <option key={year} value={year}>
                                    {year}
                                  </option>
                                ))}
                              </select>
                            </>
                          )}
                        </div>
                        <DatepickerNavButton icon={icnextMonth} label={t('next')} onClick={() => stepGregorianMonth(increaseMonth)} disabled={isBusy} />
                      </div>
                    </div>
                  );
                }}
                renderDayContents={
                  hebrewCalendar
                    ? (day, date) => {
                        const hebrew = hebrewPrimary ? formatHebrewDayFromLocalCivilDate(date) : formatHebrewFromLocalCivilDate(date);
                        if (!hebrew) return day;
                        return (
                          <span className={styles.dualDay} data-calendar-cell={hebrewPrimary ? 'hebrew' : 'gregorian'}>
                            <span className={styles.dualDayPrimary} dir={hebrewPrimary ? 'rtl' : 'ltr'}>
                              {hebrewPrimary ? hebrew : day}
                            </span>
                            <span className={styles.dualDaySecondary} dir={hebrewPrimary ? 'ltr' : 'rtl'}>
                              {hebrewPrimary ? day : hebrew}
                            </span>
                          </span>
                        );
                      }
                    : undefined
                }
              />
            </div>
          );
        }
        case 'number':
          return (
            <InputWithIcon
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={t(placeholder)}
              type="number"
              icon={icShekel}
              iconClassName={styles.shekelIcon}
            />
          );
        default:
          return <InputWithIcon value={value} onChange={(e) => setValue(e.target.value)} placeholder={t(placeholder)} type="text" />;
      }
    };

    return (
      <div className={`${styles.rangeFilter} ${filterType === 'options-and-range' ? styles.rangeFilterContainerWithBackButton : ''}`}>
        {filterType === 'options-and-range' && (
          <div className={styles.backToOptions}>
            <img
              src={icArrowRight}
              className="cursor-pointer"
              onClick={() => {
                resetRangeValuesToApplied();
                setIsCustomRangeVisible(false);
              }}
            />
          </div>
        )}
        <div className={`${styles.rangeContent} ${filterType === 'options-and-range' ? styles.rangeContentWithBackButton : ''}`}>
          <div className={styles.rangeInputsGroup}>
            <div className={styles.rangeModeSwitch} role="tablist">
              {(['range', 'exact'] as RangeMode[]).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  role="tab"
                  aria-selected={rangeMode === mode}
                  className={`${styles.rangeModeOption} ${rangeMode === mode ? styles.rangeModeOptionActive : ''} body-md-semibold`}
                  onClick={() => switchRangeMode(mode)}
                >
                  {t(mode)}
                </button>
              ))}
            </div>
            {rangeMode === 'exact' ? (
              renderRangeInput(fromValue, setFromValue, t(rangeConfig.type === 'date' ? 'exactDate' : 'exactAmount'), true)
            ) : (
              <>
                {renderRangeInput(fromValue, setFromValue, t(rangeConfig.fromPlaceholder), true)}
                {renderRangeInput(toValue, setToValue, t(rangeConfig.toPlaceholder), false)}
              </>
            )}
          </div>
          <Button
            className={`${styles.rangeApplyButton} body-md-semibold text-(--font-body-4)`}
            onClick={handleRangeApply}
            disabled={
              rangeMode === 'exact'
                ? !fromValue
                : !!(
                    (rangeConfig.type === 'date' && fromValue && toValue && new Date(fromValue) > new Date(toValue)) ||
                    (rangeConfig.type === 'number' && fromValue && toValue && Number(fromValue) > Number(toValue)) ||
                    /* An empty date range still means "everything"; an empty amount range means nothing at all. */
                    (rangeConfig.type === 'number' && !fromValue && !toValue)
                  )
            }
          >
            {t('apply')}
          </Button>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (filterType) {
      case 'options':
        return renderOptionsContent();
      case 'range':
        return renderRangeContent();
      case 'options-and-range':
        return isCustomRangeVisible ? renderRangeContent() : renderOptionsContent();
      default:
        return null;
    }
  };

  const showArrows = showMonthNavigation;

  const handlePrevMonth = () => {
    const currentDate = appliedRange ? new Date(appliedRange.from) : new Date();
    const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
    const range = getMonthRange(prevMonth);
    setFromValue(range.from);
    setToValue(range.to);
    setAppliedRange(range);
    onRangeApply?.(range.from, range.to);
  };

  const handleNextMonth = () => {
    const currentDate = appliedRange ? new Date(appliedRange.from) : new Date();
    const nextMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
    const range = getMonthRange(nextMonth);
    setFromValue(range.from);
    setToValue(range.to);
    setAppliedRange(range);
    onRangeApply?.(range.from, range.to);
  };

  return (
    <Dropdown
      trigger={
        <Button
          className={`
            ${styles.filterButton} 
            ${isOpen ? styles.filterButtonDropdownOpen : ''} 
            ${hasValue() ? styles.filterButtonHasValue : ''}
            ${showArrows ? styles.filterButtonWithArrows : ''}
          `}
        >
          {showArrows && (
            <span
              className={styles.arrowButton}
              role="button"
              tabIndex={isBusy ? -1 : 0}
              aria-label={t('previous')}
              aria-disabled={isBusy || undefined}
              data-busy={isBusy || undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (isBusy) return;
                handlePrevMonth();
              }}
              onKeyDown={(e) => {
                if (isBusy) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handlePrevMonth();
                }
              }}
            >
              <img src={icprevMonth} alt="" />
            </span>
          )}
          <span className="body-md-regular text-(--font-primary)">{getDynamicTitle()}</span>
          {showArrows && (
            <span
              className={styles.arrowButton}
              role="button"
              tabIndex={isBusy ? -1 : 0}
              aria-label={t('next')}
              aria-disabled={isBusy || undefined}
              data-busy={isBusy || undefined}
              onClick={(e) => {
                e.stopPropagation();
                if (isBusy) return;
                handleNextMonth();
              }}
              onKeyDown={(e) => {
                if (isBusy) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNextMonth();
                }
              }}
            >
              <img src={icnextMonth} alt="" />
            </span>
          )}
          <img src={icFilterExpander} alt="expander" />
        </Button>
      }
      contentAlign="start"
      setIsOpen={handleSetDropdownState}
      isOpen={isOpen}
    >
      <div className={styles.filterDropdown}>{renderContent()}</div>
    </Dropdown>
  );
};

export default OptionsRangeFilter;
